/*
 * Obermaier — Größen & Mengen (<om-bulk-sizes>)
 * Several sizes of the selected colour into the bag with one request.
 *
 * - Rows for the selected colour are rendered on the server (snippets/bulk-sizes.liquid).
 * - On om:variant:change the rows of the new colour come from ONE Section Rendering request
 *   (?option_values=…&section_id=bulk-size-rows), cached per colour; stale requests are aborted.
 * - Quantities are kept per variant while switching colours, so several colours can be combined.
 * - All copy comes from the server (JSON config + templates). No strings live in this file.
 */
import { cart, on, announce, define, formatMoney, config } from '@theme/core';

const fill = (str = '', vars = {}) =>
  Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{{ ${k} }}`, v).replaceAll(`{{${k}}}`, v), str);

/** Liquid's t filter returns HTML-escaped text; decode once (output always goes through textContent). */
const decode = (str) => {
  const area = document.createElement('textarea');
  area.innerHTML = String(str ?? '');
  return area.value;
};

const toInt = (value, fallback = 0) => {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
};

class BulkSizes extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    const configEl = this.querySelector('script[data-bulk-config]');
    if (!configEl) return;
    this.ready = true;

    try {
      this.cfg = JSON.parse(configEl.textContent);
    } catch {
      this.cfg = {};
    }
    this.s = Object.fromEntries(Object.entries(this.cfg.strings || {}).map(([k, v]) => [k, decode(v)]));
    this.uid = this.dataset.uid || 'bulk';
    this.selection = new Map(); // variantId → { qty, price, colorKey, colorName }
    this.cache = new Map(); // colour key → body HTML
    this.addButton = this.querySelector('[data-bulk-add]');
    this.addLabel = this.querySelector('[data-bulk-add-label]');
    this.addTotal = this.querySelector('[data-bulk-add-total]');
    this.bar = this.querySelector('[data-bulk-bar]');
    this.msg = this.querySelector('[data-bulk-msg]');
    this.errorEl = this.querySelector('[data-bulk-error]');
    this.others = this.querySelector('[data-bulk-others]');

    const body = this.body;
    if (body?.dataset.colorKey) this.cache.set(body.dataset.colorKey, body.outerHTML);

    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onChange);
    this.addEventListener('keydown', this.onKeydown);
    this.offVariant = on('om:variant:change', (event) => this.onVariant(event.detail || {}));

    this.syncRows();
    this.update();
    this.watchSticky();
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
    this.offVariant?.();
    this.controller?.abort();
    clearTimeout(this.doneTimer);
    this.ready = false;
  }

  /** Mark the bar as stuck (floating above the mobile dock) so it only then gets its glass panel. */
  watchSticky() {
    if (!this.classList.contains('om-bulk--sticky') || !this.bar || !('IntersectionObserver' in window)) return;
    const sentinel = document.createElement('div');
    sentinel.className = 'om-bulk__sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    this.bar.after(sentinel);
    const dock = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--om-dock-h')) || 0;
    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const below = !entry.isIntersecting && entry.boundingClientRect.top > (entry.rootBounds?.bottom ?? innerHeight);
        this.bar.classList.toggle('is-stuck', below);
      },
      { rootMargin: `0px 0px -${Math.round(dock + 9)}px 0px` }
    );
    this.stickyObserver.observe(sentinel);
  }

  get body() {
    return this.querySelector('[data-bulk-body]');
  }

  get rows() {
    return [...this.querySelectorAll('[data-bulk-row]')];
  }

  /* --------------------------------------------------------------- colour change */
  optionIdsFor(variant) {
    const names = Array.isArray(variant.options)
      ? variant.options
      : [variant.option1, variant.option2, variant.option3].filter((o) => o != null);
    const options = this.cfg.options || [];
    if (!names.length || names.length !== options.length) return null;
    const ids = options.map((option, i) => option.values?.[names[i]]);
    return ids.every(Boolean) ? ids : null;
  }

  keyFor(ids) {
    return ids.filter((_, i) => i + 1 !== this.cfg.sizePos).join('-');
  }

  onVariant({ productId, sectionId, variant }) {
    if (!variant || String(productId) !== String(this.cfg.productId)) return;
    if (sectionId && this.cfg.sectionId && String(sectionId) !== String(this.cfg.sectionId)) return;

    const ids = this.optionIdsFor(variant);
    const query = ids ? { option_values: ids.join(',') } : { variant: variant.id };
    const key = ids ? this.keyFor(ids) : `variant-${variant.id}`;
    if (key === this.body?.dataset.colorKey) return;

    const colorName = this.cfg.colorPos
      ? (variant.options?.[this.cfg.colorPos - 1] ?? variant[`option${this.cfg.colorPos}`] ?? '')
      : '';
    this.load(key, query, colorName);
  }

  async load(key, query, colorName = '') {
    this.controller?.abort();
    this.controller = null;
    this.lastRequest = { key, query, colorName };

    const cached = this.cache.get(key);
    if (cached) {
      this.swap(cached);
      return;
    }

    const controller = new AbortController();
    this.controller = controller;
    this.showSkeleton(colorName);

    try {
      const url = new URL(this.cfg.url || location.pathname, location.origin);
      for (const [param, value] of Object.entries(query)) url.searchParams.set(param, value);
      url.searchParams.set('section_id', this.cfg.rowsSection || 'bulk-size-rows');
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-bulk-body]');
      if (!fresh) throw new Error('Missing rows');
      // Shopify falls back to another variant when a combination does not exist:
      // only accept rows that belong to the requested colour.
      if (!key.startsWith('variant-') && fresh.dataset.colorKey !== key) throw new Error('Unexpected colour');
      const html = fresh.outerHTML;
      this.cache.set(key, html);
      if (key.startsWith('variant-') && fresh.dataset.colorKey) this.cache.set(fresh.dataset.colorKey, html);
      if (this.controller === controller) this.swap(html);
    } catch (error) {
      if (error.name === 'AbortError' || this.controller !== controller) return;
      this.showError();
    } finally {
      if (this.controller === controller) this.controller = null;
    }
  }

  replaceBody(node) {
    const current = this.body;
    if (current) current.replaceWith(node);
    else this.querySelector('.om-bulk__head')?.after(node);
  }

  fromTemplate(selector) {
    const tpl = this.querySelector(selector);
    return tpl ? tpl.content.firstElementChild.cloneNode(true) : null;
  }

  showSkeleton(colorName) {
    const skeleton = this.fromTemplate('template[data-bulk-skeleton]');
    if (!skeleton) return;
    const name = skeleton.querySelector('[data-bulk-skeleton-name]');
    if (name) name.textContent = colorName;
    const row = skeleton.querySelector('[data-bulk-skeleton-row]');
    const count = Math.min(Math.max(toInt(this.body?.dataset.count, 3), 2), 7);
    for (let i = 1; i < count; i += 1) row?.after(row.cloneNode(true));
    this.replaceBody(skeleton);
  }

  showError() {
    const state = this.fromTemplate('template[data-bulk-error-state]');
    if (state) this.replaceBody(state);
  }

  swap(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const fresh = tpl.content.firstElementChild;
    if (!fresh) return;
    // The rows section renders ids with the neutral "rows" scope; make them unique to this instance.
    const from = 'om-bulk-rows-';
    const to = `om-bulk-${this.uid}-`;
    const fromCaption = 'om-bulk-caption-rows';
    fresh.querySelectorAll('[id], [for], [aria-controls], [aria-labelledby]').forEach((el) => {
      for (const attr of ['id', 'for', 'aria-controls', 'aria-labelledby']) {
        const value = el.getAttribute(attr);
        if (!value) continue;
        if (value.startsWith(from)) el.setAttribute(attr, value.replace(from, to));
        else if (value === fromCaption) el.setAttribute(attr, `om-bulk-caption-${this.uid}`);
      }
    });
    this.replaceBody(fresh);
    this.syncRows();
    this.update();

    const count = toInt(fresh.dataset.count);
    if (fresh.dataset.colorName) {
      const template = count === 1 ? this.s.updatedOne : this.s.updatedOther;
      this.say(count > 0 ? fill(template, { color: fresh.dataset.colorName, count }) : this.s.noneAvailable);
    }
  }

  /* --------------------------------------------------------------- quantities */
  rule(row) {
    const min = Math.max(toInt(row.dataset.min, 1), 1);
    const step = Math.max(toInt(row.dataset.step, 1), 1);
    const max = row.dataset.max !== undefined && row.dataset.max !== '' ? toInt(row.dataset.max) : null;
    return { min, step, max };
  }

  /** Clamp to Shopify's quantity rule: 0, or ≥ min, a multiple of step, ≤ max. */
  normalize(raw, row) {
    const { min, step, max } = this.rule(row);
    let q = Math.floor(Number(raw));
    if (!Number.isFinite(q) || q <= 0) return { qty: 0, capped: false };
    let capped = false;
    if (q < min) q = min;
    if (q % step) q = Math.ceil(q / step) * step;
    if (max !== null && q > max) {
      q = Math.floor(max / step) * step;
      capped = true;
    }
    if (q < min) q = 0;
    return { qty: q, capped };
  }

  /** Record a quantity for the row's variant (kept across colour changes). */
  remember(row, qty) {
    const id = row.dataset.variantId;
    if (!id) return;
    if (qty > 0) {
      const body = this.body;
      this.selection.set(id, {
        qty,
        price: toInt(row.dataset.price),
        colorKey: body?.dataset.colorKey || '',
        colorName: body?.dataset.colorName || '',
      });
    } else {
      this.selection.delete(id);
    }
  }

  setQuantity(row, qty) {
    const input = row.querySelector('[data-bulk-qty]');
    if (!input) return;
    input.value = String(qty);
    this.remember(row, qty);
    this.syncRow(row);
  }

  syncRow(row) {
    const input = row.querySelector('[data-bulk-qty]');
    if (!input) return;
    const { max } = this.rule(row);
    const qty = toInt(input.value);
    const [minus, plus] = row.querySelectorAll('[data-bulk-step]');
    if (minus) minus.disabled = qty <= 0;
    if (plus) plus.disabled = max !== null && qty >= max;
    row.classList.toggle('is-selected', qty > 0);
    const line = row.querySelector('[data-bulk-line]');
    if (line) line.textContent = qty > 0 ? formatMoney(qty * toInt(row.dataset.price)) : '';
  }

  /** Apply remembered quantities to freshly rendered rows. */
  syncRows() {
    for (const row of this.rows) {
      const input = row.querySelector('[data-bulk-qty]');
      const entry = this.selection.get(row.dataset.variantId);
      if (input) input.value = String(entry?.qty || 0);
      this.syncRow(row);
    }
  }

  step(row, direction) {
    const input = row.querySelector('[data-bulk-qty]');
    const { min, step, max } = this.rule(row);
    const current = toInt(input?.value);
    let next;
    if (direction > 0) {
      next = current <= 0 ? min : current + step;
      if (max !== null && next > max) {
        this.say(fill(this.s.maxReached, { size: row.dataset.size || '' }));
        next = current;
      }
    } else {
      next = current - step;
      if (next < min) next = 0;
    }
    this.setQuantity(row, this.normalize(next, row).qty);
    this.clearError();
    this.update();
    // Keep focus usable when a button becomes disabled under the pointer/keyboard.
    const [minus, plus] = row.querySelectorAll('[data-bulk-step]');
    const pressed = direction > 0 ? plus : minus;
    if (pressed?.disabled && document.activeElement === pressed) input?.focus();
  }

  commitInput(input) {
    const row = input.closest('[data-bulk-row]');
    if (!row) return;
    const { qty, capped } = this.normalize(input.value, row);
    this.setQuantity(row, qty);
    if (capped) this.say(fill(this.s.maxReached, { size: row.dataset.size || '' }));
    this.update();
  }

  /* --------------------------------------------------------------- events */
  onClick = (event) => {
    const stepper = event.target.closest('[data-bulk-step]');
    if (stepper && this.contains(stepper)) {
      const row = stepper.closest('[data-bulk-row]');
      if (row) this.step(row, toInt(stepper.dataset.bulkStep));
      return;
    }
    if (event.target.closest('[data-bulk-retry]')) {
      const last = this.lastRequest;
      if (last) this.load(last.key, last.query, last.colorName);
      return;
    }
    if (event.target.closest('[data-bulk-add]')) this.add();
  };

  onInput = (event) => {
    const input = event.target.closest('[data-bulk-qty]');
    const row = input?.closest('[data-bulk-row]');
    if (!row) return;
    const { max } = this.rule(row);
    // While typing only the upper limit is enforced; the full rule applies on change.
    if (max !== null && toInt(input.value) > max) {
      this.setQuantity(row, this.normalize(max, row).qty);
      this.say(fill(this.s.maxReached, { size: row.dataset.size || '' }));
    } else {
      this.remember(row, Math.max(toInt(input.value), 0));
      this.syncRow(row);
    }
    this.clearError();
    this.update();
  };

  onChange = (event) => {
    const input = event.target.closest('[data-bulk-qty]');
    if (input) this.commitInput(input);
  };

  onKeydown = (event) => {
    // Enter inside a quantity must never submit a surrounding product form.
    if (event.key === 'Enter' && event.target.matches?.('[data-bulk-qty]')) {
      event.preventDefault();
      this.commitInput(event.target);
    }
  };

  /* --------------------------------------------------------------- totals + add */
  totals() {
    let count = 0;
    let total = 0;
    for (const entry of this.selection.values()) {
      count += entry.qty;
      total += entry.qty * entry.price;
    }
    return { count, total };
  }

  update() {
    const { count, total } = this.totals();
    if (this.addLabel) {
      this.addLabel.textContent = count > 0 ? fill(count === 1 ? this.s.addOne : this.s.addOther, { count }) : this.s.add;
    }
    if (this.addTotal) {
      this.addTotal.textContent = count > 0 ? formatMoney(total) : '';
      this.addTotal.hidden = count === 0;
    }
    this.bar?.classList.toggle('is-active', count > 0);
    this.classList.toggle('has-selection', count > 0);

    // Quantities chosen in other colours stay in the selection and are named here.
    if (this.others) {
      const currentKey = this.body?.dataset.colorKey;
      const groups = new Map();
      for (const entry of this.selection.values()) {
        if (entry.colorKey === currentKey || !entry.colorName) continue;
        groups.set(entry.colorName, (groups.get(entry.colorName) || 0) + entry.qty);
      }
      const list = [...groups].map(([name, qty]) => `${name} (${qty})`).join(', ');
      this.others.textContent = list ? fill(this.s.alsoSelected, { list }) : '';
      this.others.hidden = !list;
    }
  }

  say(message) {
    if (!this.msg) return;
    this.msg.textContent = '';
    clearTimeout(this.msgTimer);
    requestAnimationFrame(() => {
      this.msg.textContent = message;
      this.msgTimer = setTimeout(() => {
        if (this.msg.textContent === message) this.msg.textContent = '';
      }, 6000);
    });
  }

  clearError() {
    if (this.errorEl?.textContent) this.errorEl.textContent = '';
  }

  async add() {
    if (this.busy) return;
    const items = [...this.selection].map(([id, entry]) => ({ id: Number(id), quantity: entry.qty })).filter((item) => item.quantity > 0);
    this.clearError();
    if (!items.length) {
      this.say(this.s.empty);
      this.rows[0]?.querySelector('[data-bulk-qty]')?.focus();
      return;
    }

    this.busy = true;
    this.addButton?.setAttribute('aria-busy', 'true');
    const count = items.reduce((sum, item) => sum + item.quantity, 0);
    try {
      await cart.add(items, { source: 'bulk' });
      this.selection.clear();
      this.syncRows();
      this.update();
      announce(fill(count === 1 ? this.s.addedOne : this.s.addedOther, { count }));
      this.addButton?.classList.add('is-done');
      clearTimeout(this.doneTimer);
      this.doneTimer = setTimeout(() => this.addButton?.classList.remove('is-done'), 2200);
      if (config.cartType === 'page' && config.routes?.cart) window.location.assign(config.routes.cart);
    } catch (error) {
      if (this.errorEl) this.errorEl.textContent = error?.message || this.s.error;
    } finally {
      this.busy = false;
      this.addButton?.removeAttribute('aria-busy');
    }
  }
}

define('om-bulk-sizes', BulkSizes);
